import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Bell,
  CheckCheck,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShoppingBag,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { ORDER_DETAIL_PATH } from '@/shared/lib/routes';
import { useNotificationsStore } from '../store';
import { notificationsApi } from '../api';
import { notificationsKeys, useMarkAllRead, useMarkRead, useDeleteNotification, useDeleteAllNotifications } from '../hooks/useNotifications';
import { playNotificationSound, primeNotificationSound } from '../utils/sound';
import {
  getDesktopNotificationPermission,
  requestDesktopNotificationPermission,
} from '../utils/desktopNotification';
import { useQueryClient } from '@tanstack/react-query';
import type { NotificationResponse } from '../types';

interface Props {
  restaurantId: string;
  onClose: () => void;
}

export function NotificationCenter({ restaurantId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const unreadOnly = filter === 'unread';

  const items = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const nextCursor = useNotificationsStore((s) => s.nextCursor);
  const hasMore = useNotificationsStore((s) => s.hasMore);
  const connectionStatus = useNotificationsStore((s) => s.connectionStatus);
  const initialLoadState = useNotificationsStore((s) => s.initialLoadState);
  const soundUnlocked = useNotificationsStore((s) => s.soundUnlocked);
  const unlockSound = useNotificationsStore((s) => s.unlockSound);
  const appendPage = useNotificationsStore((s) => s.appendPage);
  const seedList = useNotificationsStore((s) => s.seedList);
  const setInitialLoadState = useNotificationsStore((s) => s.setInitialLoadState);

  const markRead = useMarkRead(restaurantId);
  const markAllRead = useMarkAllRead(restaurantId);
  const deleteNotification = useDeleteNotification(restaurantId);
  const deleteAll = useDeleteAllNotifications(restaurantId);
  const [refreshing, setRefreshing] = useState(false);

  const visibleItems = unreadOnly ? items.filter((x) => !x.isRead) : items;
  const isLoading = initialLoadState === 'loading' && items.length === 0;
  const isError = initialLoadState === 'error' && items.length === 0;

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const payload = await notificationsApi.listNotifications(restaurantId, {
        unreadOnly,
        limit: 20,
      });
      seedList(payload);
      setInitialLoadState('ok');
      queryClient.setQueryData(notificationsKeys.list(restaurantId, unreadOnly), payload);
    } catch {
      toast.error(t('settings.notifications.refreshError'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleRowClick = (n: NotificationResponse) => {
    markRead.mutate(n.notificationId);
    navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId));
    onClose();
  };

  const handleMarkAll = () => {
    markAllRead.mutate(undefined, {
      onSuccess: (res) => {
        if (res.marked > 0) {
          toast.success(t('settings.notifications.markedRead', { count: res.marked }));
        }
      },
    });
  };

  const handleDeleteOne = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    deleteNotification.mutate(notificationId, {
      onError: () => toast.error(t('settings.notifications.deleteError')),
    });
  };

  const handleClearAll = () => {
    deleteAll.mutate(undefined, {
      onSuccess: (res) => {
        toast.success(
          res.deleted > 0
            ? t('settings.notifications.cleared', { count: res.deleted })
            : t('settings.notifications.nothingToClear'),
        );
      },
      onError: () => toast.error(t('settings.notifications.deleteError')),
    });
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await notificationsApi.listNotifications(restaurantId, {
        unreadOnly,
        limit: 20,
        cursor: nextCursor,
      });
      appendPage({ items: page.items, nextCursor: page.nextCursor, hasMore: page.hasMore });
    } catch {
      toast.error(t('settings.notifications.loadMoreError'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleEnableSound = async () => {
    const ok = await primeNotificationSound();
    if (ok) {
      unlockSound();
    } else {
      toast.error(t('settings.notifications.soundError'));
    }
    const before = getDesktopNotificationPermission();
    if (before === 'default') {
      const after = await requestDesktopNotificationPermission();
      if (after === 'granted') {
        toast.success(t('settings.notifications.desktopEnabled'));
      }
    }
  };

  const handleTestRing = () => {
    playNotificationSound();
  };

  return (
    <div className="flex flex-col w-[360px] max-w-[calc(100vw-1rem)] max-h-[70vh]">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-sm truncate">{t('settings.notifications.title')}</span>
          <span
            aria-label={t('settings.notifications.connectionStatus', { status: connectionStatus })}
            className={cn(
              'w-2 h-2 rounded-full shrink-0',
              connectionStatus === 'open' && 'bg-success',
              connectionStatus === 'connecting' && 'bg-amber-500',
              connectionStatus === 'closed' && 'bg-destructive',
              connectionStatus === 'idle' && 'bg-muted-foreground/40',
            )}
          />
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {soundUnlocked ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleTestRing}
              aria-label={t('settings.notifications.testRing')}
              title={t('settings.notifications.testRing')}
            >
              <Bell className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleEnableSound}
            >
              {t('settings.notifications.enableSound')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label={t('common.refreshing')}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border gap-2">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | 'unread')}>
          <TabsList className="h-7">
            <TabsTrigger value="all" className="text-xs h-5 px-2.5">
              {t('settings.notifications.all')}
            </TabsTrigger>
            <TabsTrigger value="unread" className="text-xs h-5 px-2.5">
              {t('settings.notifications.unread')}{unreadCount > 0 ? ` (${unreadCount > 99 ? '99+' : unreadCount})` : ''}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleMarkAll}
            disabled={unreadCount === 0 || markAllRead.isPending}
            aria-label={t('settings.notifications.markAllRead')}
            title={t('settings.notifications.markAllRead')}
          >
            <CheckCheck className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={handleClearAll}
            disabled={items.length === 0 || deleteAll.isPending}
            aria-label={t('settings.notifications.clearAll')}
            title={t('settings.notifications.clearAll')}
          >
            {deleteAll.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-[160px] overflow-y-auto overflow-x-hidden">
        {isLoading ? (
          <div className="p-3 space-y-2">
            <Skeleton className="h-14 w-full rounded-md" />
            <Skeleton className="h-14 w-full rounded-md" />
            <Skeleton className="h-14 w-full rounded-md" />
          </div>
        ) : isError ? (
          <div className="p-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">{t('settings.notifications.couldntLoad')}</p>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              {t('common.retry')}
            </Button>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="p-8 text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-muted mx-auto flex items-center justify-center">
              <Bell className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">{t('settings.notifications.allCaughtUp')}</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {visibleItems.map((n) => {
              const style = iconForEvent(n.eventType);
              return (
                <li key={n.notificationId}>
                  <button
                    type="button"
                    onClick={() => handleRowClick(n)}
                    className={cn(
                      'w-full text-left px-4 py-3 flex gap-3 hover:bg-muted/60 focus:bg-muted/60 focus:outline-none transition-colors group',
                      !n.isRead && 'bg-accent/5'
                    )}
                  >
                    <div
                      aria-hidden
                      className={cn(
                        'mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0',
                        style.bg
                      )}
                    >
                      <style.Icon className={cn('h-3.5 w-3.5', style.fg)} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-foreground truncate">{n.title}</p>
                        {n.autoAcceptedBySystem && (
                          <span
                            aria-label={t('settings.notifications.autoAccepted')}
                            className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
                          >
                            <Zap className="h-2.5 w-2.5" /> Auto
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                      <p className="text-[11px] text-muted-foreground/80 mt-1">
                        {formatRelative(n.createdAt, i18n.language)}
                      </p>
                    </div>
                    <div className="flex items-start gap-1 shrink-0">
                      {!n.isRead && (
                        <span
                          aria-label={t('settings.notifications.unreadIndicator')}
                          className="mt-2 h-2 w-2 rounded-full bg-accent"
                        />
                      )}
                      <button
                        type="button"
                        onClick={(e) => handleDeleteOne(e, n.notificationId)}
                        aria-label={t('settings.notifications.deleteNotification')}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                        disabled={deleteNotification.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {hasMore && visibleItems.length > 0 && (
          <div className="p-3">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : null}
              {t('settings.notifications.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatRelative(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return formatDistanceToNowStrict(d, { addSuffix: true, locale: lang === 'de' ? de : undefined });
  } catch {
    return '';
  }
}

interface EventStyle {
  Icon: typeof Bell;
  fg: string;
  bg: string;
}

function iconForEvent(eventType: string): EventStyle {
  switch (eventType) {
    case 'ORDER_PLACED':
      return { Icon: ShoppingBag, fg: 'text-accent', bg: 'bg-accent/10' };
    case 'ORDER_ACCEPTED':
      return { Icon: CheckCircle2, fg: 'text-success', bg: 'bg-success/10' };
    case 'ORDER_REJECTED':
      return { Icon: XCircle, fg: 'text-destructive', bg: 'bg-destructive/10' };
    default:
      return { Icon: Bell, fg: 'text-muted-foreground', bg: 'bg-muted' };
  }
}
